import { useQuery } from '@tanstack/react-query'
import { authorizedFetch } from '@/lib/api'
import { useState, useEffect } from 'react'

import type { ThreadFilePreview, ThreadObjectPreview } from '@/hooks/use-threads'

export interface ThreadSearchResult {
  id: string
  subject: string | null
  user_id: string | null
  created_at: string
  updated_at: string
  message_count: number
  summary: string | null
  activity?: Record<string, number> | null
  latest_object_update?: {
    object_type: string
    object_id: string
    object_name?: string
    action: string
    source_tool?: string
    text?: string
  } | null
  updates_digest?: string[] | null
  file_count?: number
  files?: ThreadFilePreview[]
  object_count?: number
  objects?: ThreadObjectPreview[]
  feedback_items: Array<{
    score: number
    tags: string[]
    comment: string
    at: string | null
    message_id: string
    run_id: string
  }> | null
  similarity_score: number
  match_type: 'semantic' | 'keyword' | 'both'
  match_reasons: string[]
  matched_fields: string[]
}

export interface ThreadSearchResponse {
  results: ThreadSearchResult[]
  total: number
  query: string
}

export function useThreadSearch(query: string, enabled: boolean = true) {
  const [previousQuery, setPreviousQuery] = useState<string>('')
  const [queryChanged, setQueryChanged] = useState<boolean>(false)

  const { data, isLoading, error, isFetching } = useQuery<ThreadSearchResponse>({
    queryKey: ['threads', 'search', query],
    queryFn: async () => {
      if (!query.trim()) {
        return { results: [], total: 0, query: '' }
      }
      
      const response = await authorizedFetch(`/api/threads/search?q=${encodeURIComponent(query)}`)
      if (!response.ok) {
        throw new Error('Failed to search threads')
      }
      return await response.json()
    },
    enabled: enabled && query.trim().length > 0,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  })

  // Track query changes
  useEffect(() => {
    if (query && query !== previousQuery && previousQuery !== '') {
      // Query changed - check if previous search was in progress
      setQueryChanged(true)
    } else if (query === previousQuery || previousQuery === '') {
      setQueryChanged(false)
    }
    
    if (query !== previousQuery) {
      setPreviousQuery(query)
    }
  }, [query, previousQuery])

  return {
    results: data?.results || [],
    total: data?.total || 0,
    query: data?.query || '',
    isLoading: isLoading || isFetching,
    error,
    previousQuery,
    queryChanged,
  }
}
